import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { getProperty } from "../../actions/property-actions"
import { updateRequest } from "../../actions/request-actions"
import { useApp } from "../../context/useApp"
import { alert } from "../../components/Alert"
import {
  FaUser,
  FaHome,
  FaBriefcase,
  FaTools,
  FaAward,
  FaPhone,
  FaBuilding,
  FaCar,
  FaDollarSign,
  FaUsers,
} from "react-icons/fa"
import {
  documentTypes,
  provinces,
  requestTypes,
  referenceTypes,
  actualPropertyTypes,
  vehicleTypes,
} from "../../actions/global-actions-data"
import {
  propertyTypes,
  finantialInformationType,
} from "../properties/create-property-data"
import {
  requestEditableSections,
  editDynamicArraysConfig,
} from "./edit-request-data"

const SECTION_ICONS = {
  "Datos personales": <FaUser className="me-2" />,
  "Vivienda actual": <FaHome className="me-2" />,
  Empleado: <FaBriefcase className="me-2" />,
  Independiente: <FaTools className="me-2" />,
  Pensionado: <FaAward className="me-2" />,
  Referencias: <FaPhone className="me-2" />,
  Propiedades: <FaBuilding className="me-2" />,
  Vehículos: <FaCar className="me-2" />,
  "Información Financiera": <FaDollarSign className="me-2" />,
  Codeudores: <FaUsers className="me-2" />,
}

const ARRAY_TEMPLATES = {
  cosigners: {
    firstName: "",
    lastName: "",
    documentType: "",
    document: "",
    email: "",
    phone: "",
    cosignerProvince: "",
    cosignerCity: "",
    address: "",
  },
  properties: {
    type: "",
    address: "",
    propertyProvince: "",
    propertyCity: "",
    value: "",
    realEstateRegistration: "",
  },
  vehicles: { type: "", brand: "", model: "", plaque: "" },
  finantialInformation: {
    type: "",
    entity: "",
    value: "",
    fee: "",
    remaining: "",
  },
}

function EditRequest() {
  const navigate = useNavigate()
  const { user, setLoading } = useApp()
  const [loadData, setLoadData] = useState(true)
  const params = useParams()
  const [currentData, setCurrentData] = useState({})
  const [currentRequest, setCurrentRequest] = useState({})
  const [form, setForm] = useState({})
  const [canShow, setCanShow] = useState(false)
  const [citiesData, setCitiesData] = useState({
    actualPropertyCities: [],
    propertiesCities: [],
    cosignersCities: [],
  })

  // Helper: Get/set nested values
  const getNestedValue = (obj, path) => {
    // First try to get as a flat key (e.g., "employee.company")
    if (obj[path] !== undefined) {
      return obj[path]
    }
    // Then try to get as nested (e.g., obj.employee.company)
    return path.split(".").reduce((acc, part) => acc?.[part], obj) ?? ""
  }

  const setNestedValue = (obj, path, value) => {
    const parts = path.split(".")
    let current = obj
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]]) current[parts[i]] = {}
      current = current[parts[i]]
    }
    current[parts[parts.length - 1]] = value
  }

  // Ensure references always have 4 items with specific types
  const ensureReferencesStructure = (references) => {
    const referenceTypes = ["Familiar", "Personal", "Comercial", "Comercial"]
    const existingRefs = Array.isArray(references) ? references : []

    return referenceTypes.map((type, idx) => {
      const existing = existingRefs.find(
        (r) =>
          r.type === type && !referenceTypes.slice(0, idx).includes(r.type),
      )
      const ref = existing || { type, name: "", phone: "", email: "" }
      // Remove relationship field for commercial references
      if (type === "Comercial") {
        delete ref.relationship
      }
      return ref
    })
  }

  // Ensure minimum cosigners based on monthly cost
  const ensureCosignersStructure = (cosigners, monthlyCost) => {
    const minRequired = monthlyCost > 1000000 ? 2 : 1
    const existingCosigners = Array.isArray(cosigners) ? cosigners : []

    // If we already have enough, return as is
    if (existingCosigners.length >= minRequired) {
      return existingCosigners
    }

    // Add empty cosigners up to minimum required with deep copies
    const result = [...existingCosigners]
    for (let i = existingCosigners.length; i < minRequired; i++) {
      result.push(JSON.parse(JSON.stringify(ARRAY_TEMPLATES.cosigners)))
    }
    return result
  }

  // Initialize cities data
  const initializeCitiesData = (initialForm) => {
    const citiesData = {
      actualPropertyCities: [],
      propertiesCities: [],
      cosignersCities: [],
    }

    if (initialForm["actualProperty.actualPropertyProvince"]) {
      citiesData.actualPropertyCities =
        provinces.find(
          (p) =>
            p.name === initialForm["actualProperty.actualPropertyProvince"],
        )?.cities || []
    }

    if (initialForm.properties?.length) {
      citiesData.propertiesCities = initialForm.properties.map(
        (prop) =>
          provinces.find((p) => p.name === prop.propertyProvince)?.cities || [],
      )
    }

    if (initialForm.cosigners?.length) {
      citiesData.cosignersCities = initialForm.cosigners.map(
        (c) =>
          provinces.find((p) => p.name === c.cosignerProvince)?.cities || [],
      )
    }

    return citiesData
  }

  // Load data effect
  useEffect(() => {
    if (!loadData) return

    const loadRequest = async () => {
      try {
        const data = await getProperty(params.id, setLoading, user.token)
        if (data.code === "01") {
          alert("Error", data.message)
          return
        }

        const info = data.data[0]

        console.log("Loaded property data:", info)
        const requestObj = info.requests.find((r) => r._id === params.requestID)

        if (!requestObj) {
          alert("Error", "No existe una solicitud con los datos proporcionados")
          return
        }

        const initialForm = {}
        requestEditableSections.forEach((section) => {
          section.fields.forEach((field) => {
            if (field.type === "array") {
              initialForm[field.name] = Array.isArray(requestObj[field.name])
                ? requestObj[field.name]
                : []
            } else {
              initialForm[field.name] = getNestedValue(requestObj, field.name)
            }
          })
        })

        // Ensure references always have 4 items with specific types
        initialForm.references = ensureReferencesStructure(
          initialForm.references,
        )

        // Ensure minimum cosigners based on monthly cost
        initialForm.cosigners = ensureCosignersStructure(
          initialForm.cosigners,
          info.monthCost,
        )

        setCurrentData(info)
        setCurrentRequest(requestObj)
        setForm(initialForm)
        setCitiesData(initializeCitiesData(initialForm))
        setCanShow(true)
      } catch (error) {
        console.error("Error fetching property:", error)
      } finally {
        setLoading(false)
      }
    }

    setLoadData(false)
    loadRequest()
  }, [loadData, params.id, user.token, params.requestID, setLoading])

  // Get select options
  const getSelectOptions = (optionsKey, fieldConfig = {}) => {
    const optionsMap = {
      documentTypes: documentTypes.map((t) => ({
        value: t.value,
        label: t.name,
      })),
      provinces: provinces.map((p) => ({ value: p.name, label: p.name })),
      requestTypes: requestTypes.map((t) => ({ value: t, label: t })),
      referenceTypes: referenceTypes.map((t) => ({ value: t, label: t })),
      actualPropertyTypes: actualPropertyTypes.map((t) => ({
        value: t,
        label: t,
      })),
      vehicleTypes: vehicleTypes.map((t) => ({ value: t, label: t })),
      propertyTypes: propertyTypes.map((t) => ({ value: t, label: t })),
      finantialInformationType: finantialInformationType.map((t) => ({
        value: t,
        label: t,
      })),
    }

    if (optionsKey === "cities") {
      const parentValue = fieldConfig.parentField
        ? getNestedValue(form, fieldConfig.parentField)
        : ""
      const province = provinces.find((p) => p.name === parentValue)
      return province
        ? province.cities.map((c) => ({ value: c.name, label: c.name }))
        : []
    }

    return optionsMap[optionsKey] || []
  }

  const isEditingDisabled =
    currentRequest.status === "En contrato" ||
    currentRequest.status === "Terminado"

  // Handle input changes
  const handleInputChange = (event) => {
    const { name, value } = event.target
    // Only apply numeric filter for specific document-related fields, not documentType
    const finalValue = /^(phone|tel|document)$/i.test(name)
      ? value.replace(/[^0-9]/g, "")
      : value

    setForm({ ...form, [name]: finalValue })
  }

  // Handle select changes for normal fields
  const handleSelectChange = (event) => {
    const { name, value } = event.target

    // Special handling for province fields that need to update related city fields
    if (name === "actualProperty.actualPropertyProvince") {
      const cities = value
        ? provinces.find((p) => p.name === value)?.cities || []
        : []

      setForm({
        ...form,
        [name]: value,
        "actualProperty.actualPropertyCity": "",
      })
      setCitiesData({ ...citiesData, actualPropertyCities: cities })
      return
    }

    // Regular select change
    setForm({ ...form, [name]: value })
  }

  // Handle select changes for dynamic array fields
  const handleDynamicSelectChange = (field, citiesArrayName, index, event) => {
    const { name, value } = event.target
    const isProvinceField = name.includes("Province")

    if (isProvinceField) {
      const cities = value
        ? provinces.find((p) => p.name === value)?.cities || []
        : []

      let newCitiesArray = [...citiesData[citiesArrayName]]
      newCitiesArray[index] = cities
      setCitiesData({ ...citiesData, [citiesArrayName]: newCitiesArray })

      let newArray = [...form[field]]
      newArray[index][name] = value
      newArray[index][name.replace("Province", "City")] = ""

      setForm({ ...form, [field]: newArray })
    } else {
      handleArrayFieldChange(field, index, name, value)
    }
  }

  const handleArrayFieldChange = (arrayName, index, fieldName, value) => {
    // Only apply numeric filter for specific fields (phone, tel, document number), not documentType
    const finalValue = /^(phone|tel|document)$/i.test(fieldName)
      ? value.replace(/[^0-9]/g, "")
      : value

    const updatedArray = [...(form[arrayName] || [])]
    if (!updatedArray[index]) updatedArray[index] = {}
    updatedArray[index][fieldName] = finalValue

    setForm({ ...form, [arrayName]: updatedArray })
  }

  const handleAddArrayItem = (arrayName) => {
    const updatedArray = [...(form[arrayName] || [])]
    // Deep copy the template to avoid sharing the same object reference
    const newItem = JSON.parse(JSON.stringify(ARRAY_TEMPLATES[arrayName] || {}))
    updatedArray.push(newItem)
    setForm({ ...form, [arrayName]: updatedArray })
  }

  const handleRemoveArrayItem = (arrayName, index) => {
    const monthCost = currentData.monthCost || 0
    if (
      arrayName === "cosigners" &&
      monthCost > 1000000 &&
      form.cosigners.length <= 2
    ) {
      alert(
        "Advertencia",
        "Debe haber al menos dos codeudores para este inmueble",
      )
      return
    }
    if (arrayName === "cosigners" && form[arrayName].length <= 1) {
      alert("Advertencia", "Debe haber al menos un codeudor")
      return
    }
    const updatedArray = form[arrayName].filter((_, i) => i !== index)
    setForm({ ...form, [arrayName]: updatedArray })
  }

  // Form submission
  const handleOnSubmit = (event) => {
    event.preventDefault()

    if (isEditingDisabled) {
      alert(
        "Advertencia",
        "No puede modificar una solicitud que está en contrato o ha sido terminada",
      )
      return
    }

    const approvedRequest = currentData.requests.find(
      (r) => r.status === "Aprobado",
    )
    if (approvedRequest?.contracts?.some((c) => c.status === "Activo")) {
      alert(
        "Advertencia",
        "No puede modificar una solicitud mientras exista un contrato activamente activo",
      )
      return
    }

    console.log("currentData", currentData)

    const minCosigners = currentData.monthCost > 1000000 ? 2 : 1
    const activeCosigners = (form.cosigners || []).filter(
      (c) => c.firstName || c.lastName || c.document,
    )
    if (activeCosigners.length < minCosigners) {
      alert(
        "Advertencia",
        `Debe agregar mínimo ${minCosigners} codeudor(es) diligenciado(s)`,
      )
      return
    }

    if (!window.confirm("¿Realmente desea actualizar esta solicitud?")) return

    const data = JSON.parse(JSON.stringify(currentData))
    const requestToUpdate = data.requests.find(
      (r) => r._id === params.requestID,
    )

    if (requestToUpdate) {
      requestEditableSections.forEach((section) => {
        section.fields.forEach((field) => {
          if (field.type === "array") {
            requestToUpdate[field.name] = form[field.name] || []
          } else {
            setNestedValue(requestToUpdate, field.name, form[field.name])
          }
        })
      })
    }

    ;(async () => {
      try {
        const result = await updateRequest({ data }, setLoading, user.token)
        if (result.code === "01") {
          alert("Error", result.message)
        } else {
          alert("¡Éxito!", "Solicitud actualizada con éxito")
          navigate(-1)
        }
      } catch (error) {
        console.error("Error updating request:", error)
      } finally {
        setLoading(false)
      }
    })()
  }

  // Render field input
  const renderFieldInput = (field, value, onChange) => {
    const commonProps = {
      id: field.name,
      className: "input-field",
      disabled: isEditingDisabled,
    }

    switch (field.type) {
      case "select":
        return (
          <select
            {...commonProps}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">Seleccionar...</option>
            {getSelectOptions(field.optionsKey, field).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )
      case "textarea":
        return (
          <>
            <textarea
              {...commonProps}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              rows={field.rows || 4}
              maxLength={field.maxLength}
            />
            {field.maxLength && (
              <small style={{ marginTop: "5px", color: "#666" }}>
                {value.length}/{field.maxLength} caracteres
              </small>
            )}
          </>
        )
      case "date":
        return (
          <input
            {...commonProps}
            type="date"
            value={value ? new Date(value).toISOString().split("T")[0] : ""}
            onChange={(e) => onChange(e.target.value)}
          />
        )
      case "number":
        return (
          <input
            {...commonProps}
            type="number"
            min={field.min || 0}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        )
      default:
        return (
          <input
            {...commonProps}
            type={field.type || "text"}
            maxLength={field.maxLength}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            autoComplete="off"
          />
        )
    }
  }

  // Render normal field
  const renderField = (field) => {
    if (field.type === "array") return null

    const fieldValue = getNestedValue(form, field.name)
    return (
      <div
        key={field.name}
        className={`d-flex flex-column ${field.width} field`}
      >
        <label className="label-field" htmlFor={field.name}>
          {field.label}
        </label>
        {renderFieldInput(field, fieldValue, (val) =>
          field.type === "select"
            ? handleSelectChange({ target: { name: field.name, value: val } })
            : handleInputChange({ target: { name: field.name, value: val } }),
        )}
      </div>
    )
  }

  // Render array item field
  const renderArrayField = (
    field,
    value,
    onChange,
    fieldKey,
    citiesArray,
    arrayName,
    item,
  ) => {
    // Disable type field for references
    if (arrayName === "references" && field.name === "type") {
      return (
        <input
          id={fieldKey}
          type="text"
          className="input-field"
          value={value}
          disabled
        />
      )
    }

    // Hide relationship field for commercial references
    if (
      arrayName === "references" &&
      field.name === "relationship" &&
      item?.type === "Comercial"
    ) {
      return null
    }

    if (field.optionsKey === "cities" && citiesArray) {
      return (
        <select
          id={fieldKey}
          className="input-field"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={isEditingDisabled}
        >
          <option value="">Seleccionar...</option>
          {citiesArray.map((city) => (
            <option key={city.name} value={city.name}>
              {city.name}
            </option>
          ))}
        </select>
      )
    }

    return renderFieldInput(field, value, onChange)
  }

  // Render array section
  const renderArraySection = (
    sectionTitle,
    arrayName,
    itemLabel,
    config,
    citiesKey,
  ) => {
    const items = form[arrayName] || []

    return (
      <div key={sectionTitle} className="w-100">
        <h4 className="w-100 my-3">
          {SECTION_ICONS[sectionTitle]}
          {sectionTitle}
        </h4>

        {items.length > 0 ? (
          <>
            {items.map((item, index) => (
              <div
                key={index}
                className="w-100 mb-4 p-3 rounded"
                style={{ backgroundColor: "#f8f9ff", borderLeft: "4px solid" }}
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5>
                    {itemLabel} {index + 1}
                  </h5>
                  {arrayName !== "references" && (
                    <button
                      type="button"
                      onClick={() => handleRemoveArrayItem(arrayName, index)}
                      className="btn btn-sm btn-danger"
                      disabled={isEditingDisabled}
                    >
                      Eliminar
                    </button>
                  )}
                </div>
                <div className="d-flex w-100 flex-wrap">
                  {config.fields.map((field) => {
                    const fieldKey = `${field.name}_${index}`
                    const fieldValue = item[field.name] || ""
                    const citiesArray =
                      citiesKey && citiesData[citiesKey]
                        ? citiesData[citiesKey][index]
                        : null

                    const rendered = renderArrayField(
                      field,
                      fieldValue,
                      (val) =>
                        field.type === "select"
                          ? handleDynamicSelectChange(
                              arrayName,
                              citiesKey,
                              index,
                              {
                                target: { name: field.name, value: val },
                              },
                            )
                          : handleArrayFieldChange(
                              arrayName,
                              index,
                              field.name,
                              val,
                            ),
                      fieldKey,
                      citiesArray,
                      arrayName,
                      item,
                    )

                    if (!rendered) return null

                    return (
                      <div
                        key={fieldKey}
                        className={`d-flex flex-column ${field.width} field`}
                      >
                        <label className="label-field" htmlFor={fieldKey}>
                          {field.label}
                        </label>
                        {rendered}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </>
        ) : (
          <p className="text-muted">
            No hay {itemLabel.toLowerCase()} agregad
            {itemLabel.endsWith("r") ? "o" : "a"}s
          </p>
        )}

        {arrayName !== "references" && (
          <button
            type="button"
            onClick={() => handleAddArrayItem(arrayName)}
            className="btn btn-sm btn-secondary mb-4"
            disabled={isEditingDisabled}
          >
            + Agregar {itemLabel}
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="w-100 d-flex">
      {canShow && currentRequest && Object.keys(currentRequest).length > 0 ? (
        <div className="container d-flex flex-column">
          <div className="w-100 mt-5">
            <button onClick={() => navigate(-1)} className="back-button">
              &#60; Volver
            </button>
          </div>
          <h1 className="my-5">
            Editar solicitud - Inmueble {currentData.code}
          </h1>

          {isEditingDisabled && (
            <div className="alert alert-warning w-100 mb-4">
              <strong>Atención:</strong> No puede editar esta solicitud porque
              está en estado "{currentRequest.status}".
            </div>
          )}

          <form onSubmit={handleOnSubmit}>
            <div className="d-flex w-100 flex-wrap">
              {requestEditableSections.map((section, idx) => {
                // Check if the section is visible based on its condition
                if (section.condition && !section.condition(form)) {
                  return null
                }

                const arrayConfigs = {
                  Referencias: [
                    "Referencias",
                    "references",
                    "Referencia",
                    editDynamicArraysConfig.references,
                    null,
                  ],
                  Codeudores: [
                    "Codeudores",
                    "cosigners",
                    "Codeudor",
                    editDynamicArraysConfig.cosigners,
                    "cosignersCities",
                  ],
                  Propiedades: [
                    "Propiedades",
                    "properties",
                    "Propiedad",
                    editDynamicArraysConfig.properties,
                    "propertiesCities",
                  ],
                  Vehículos: [
                    "Vehículos",
                    "vehicles",
                    "Vehículo",
                    editDynamicArraysConfig.vehicles,
                    null,
                  ],
                  "Información Financiera": [
                    "Información Financiera",
                    "finantialInformation",
                    "Información Financiera",
                    editDynamicArraysConfig.finantialInformation,
                    null,
                  ],
                }

                if (arrayConfigs[section.title]) {
                  const [title, arrayName, itemLabel, config, citiesKey] =
                    arrayConfigs[section.title]
                  return renderArraySection(
                    title,
                    arrayName,
                    itemLabel,
                    config,
                    citiesKey,
                  )
                }

                const visibleFields = section.fields.filter(
                  (f) => !f.condition || f.condition(form),
                )

                return visibleFields.length > 0 ? (
                  <div key={idx} className="w-100">
                    <h4 className="w-100 my-3">
                      {SECTION_ICONS[section.title]}
                      {section.title}
                    </h4>
                    <div className="d-flex w-100 flex-wrap">
                      {visibleFields.map((field) => renderField(field))}
                    </div>
                  </div>
                ) : null
              })}

              <div className="w-100 d-flex justify-content-center my-4">
                <button
                  className="primary-button save-button"
                  type="submit"
                  disabled={isEditingDisabled}
                >
                  Actualizar solicitud
                </button>
              </div>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  )
}

export default EditRequest
